import { useCallback, useEffect, useState } from 'react'
import { AlertCircle, Calendar, CheckCircle, Clock, Eye, MapPin, PauseCircle, RefreshCw, Send, Users, XCircle } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../hooks/useAuth'
import {
  alerteSemaine,
  erreurEnClair,
  heure,
  joursEntre,
  jourCourt,
  libelleJours,
  libelleSituation,
  parSemaine,
  resumeLancement,
  situation,
  type EtatPreSession,
  type Lancement,
  type SemainePreSession,
  type SessionPreSession,
} from '../../../lib/communication/preSession'
import { TestPreSession } from './TestPreSession'

/**
 * L'onglet « Messages pré-session ».
 *
 * Le déroulé : un clic lance la première vague d'une semaine ; ensuite, chaque
 * matin à 8 h, un rattrapage sert les inscrits tardifs des sessions lancées,
 * jusqu'au premier jour. Une session qui atteint le seuil après le clic attend
 * une approbation. Sans clic, rien ne part : l'écran alerte.
 *
 * L'écran n'envoie rien lui-même : le clic planifie, et l'expéditeur
 * `traiter-envois-pre-session` envoie au rythme que Gmail tolère.
 */

/*
  « Forcer l'envoi » sert une session sous le seuil : l'administration la force
  (`choisir_envoi_pre_session`) puis la lance dans la foulée. « Suspendre »
  existe en base mais reste inactif, avec sa raison.
*/
const RAISON_SUSPENDRE_FERME = 'Décision en attente : suspendre une session'

function aujourdHuiBruxelles(): string {
  return new Intl.DateTimeFormat('fr-CA', { timeZone: 'Europe/Brussels' }).format(new Date())
}

function dateHeure(iso: string): string {
  return new Date(iso).toLocaleString('fr-BE', {
    timeZone: 'Europe/Brussels', weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  })
}

export function EnvoisPreSession() {
  const { userProfile } = useAuth()
  const isAdmin = userProfile?.role === 'admin'

  const [etat, setEtat] = useState<EtatPreSession | null>(null)
  const [chargement, setChargement] = useState(true)
  const [erreur, setErreur] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [action, setAction] = useState<string | null>(null)
  const [enTest, setEnTest] = useState<SessionPreSession | null>(null)

  const charger = useCallback(async () => {
    setChargement(true)
    const { data, error } = await supabase.rpc('etat_envois_pre_session', { p_jours: 14 })
    if (error) {
      setErreur(erreurEnClair(error))
      setEtat(null)
    } else {
      setErreur(null)
      setEtat(data as EtatPreSession)
    }
    setChargement(false)
  }, [])

  useEffect(() => {
    charger()
  }, [charger])

  const lancer = async (debut: string, session?: SessionPreSession, confirmation?: string) => {
    if (!etat) return
    if (confirmation && !window.confirm(confirmation)) return
    setAction(session ? session.session_id : debut)
    setMessage(null)
    setErreur(null)
    const { data, error } = await supabase.rpc('lancer_vague_pre_session', {
      p_debut: debut,
      p_session_id: session?.session_id ?? null,
    })
    if (error) {
      setErreur(erreurEnClair(error))
    } else {
      setMessage(resumeLancement(data as Lancement, etat.reglages))
      await charger()
    }
    setAction(null)
  }

  const forcer = async (s: SessionPreSession) => {
    if (!etat) return
    const confirmation =
      `Envoyer le message pré-session aux ${s.familles_attendues} famille(s) de ${s.stage_title ?? 'cette session'} (${s.center_name})`
      + ` alors que la session est sous le seuil de ${etat.reglages.seuil} enfants attendus ?`
    if (!window.confirm(confirmation)) return
    setAction(s.session_id)
    setMessage(null)
    setErreur(null)
    const choix = await supabase.rpc('choisir_envoi_pre_session', {
      p_session_id: s.session_id,
      p_choix: 'forcer',
      p_motif: 'Envoi forcé sous le seuil',
    })
    if (choix.error) {
      setErreur(erreurEnClair(choix.error))
      setAction(null)
      return
    }
    const { data, error } = await supabase.rpc('lancer_vague_pre_session', {
      p_debut: s.start_date.slice(0, 10),
      p_session_id: s.session_id,
    })
    if (error) {
      // Le lancement a échoué : la session revient à la règle, pour ne pas partir plus tard à l'insu de tous.
      await supabase.rpc('choisir_envoi_pre_session', { p_session_id: s.session_id, p_choix: null })
      setErreur(erreurEnClair(error))
    } else {
      setMessage(resumeLancement(data as Lancement, etat.reglages))
    }
    await charger()
    setAction(null)
  }

  const regler = async (params: { p_suspendre?: boolean; p_lever_la_pause?: boolean }) => {
    setAction('reglage')
    const { error } = await supabase.rpc('regler_envois_pre_session', params)
    if (error) setErreur(erreurEnClair(error))
    else await charger()
    setAction(null)
  }

  if (chargement && !etat) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (!etat) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800 flex items-start">
        <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
        <span>{erreur ?? 'État des envois illisible.'}</span>
      </div>
    )
  }

  const r = etat.reglages
  const aujourdHui = aujourdHuiBruxelles()
  const semaines = parSemaine(etat.sessions)
  const alertes = semaines.map(s => alerteSemaine(s, aujourdHui, r)).filter((a): a is string => a !== null)

  const boutonDeSemaine = (s: SemainePreSession) => {
    if (s.aLancer.length === 0) return null
    const libelle = s.lancee
      ? `Approuver les ${s.aLancer.length} session(s) qui ont atteint le seuil`
      : `Lancer la première vague — ${s.aLancer.length} session(s), ${s.famillesALancer} famille(s)`
    const confirmation = s.lancee
      ? `Approuver l'envoi pour ${s.aLancer.length} session(s) de la semaine du ${jourCourt(s.debut)} ?`
      : `Lancer la première vague de la semaine du ${jourCourt(s.debut)} : ${s.aLancer.length} session(s), ${s.famillesALancer} famille(s) ? Le rattrapage suivra chaque matin à ${heure(r.heure_rattrapage)}.`
    return (
      <button
        onClick={() => lancer(s.debut, undefined, confirmation)}
        disabled={action !== null || r.envois_suspendus}
        className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
      >
        {action === s.debut ? (
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
        ) : (
          <Send className="h-4 w-4" />
        )}
        <span>{libelle}</span>
      </button>
    )
  }

  return (
    <div className="space-y-6">
      {enTest && <TestPreSession session={enTest} onClose={() => setEnTest(null)} />}

      {/* La règle, le frein, la pause */}
      <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Envoi des informations avant le stage</h3>
            <p className="text-sm text-gray-600 mt-1">
              Un clic lance la première vague d'une semaine (sessions d'au moins {r.seuil} enfants attendus). Ensuite, rattrapage
              automatique chaque matin à {heure(r.heure_rattrapage)} pour les inscrits tardifs, jusqu'au premier jour du stage.
              Envois de {heure(r.heure_debut_envois)} à {heure(r.heure_fin_envois)}, week-end compris.
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Ne comptent pas : les annulations et les demandes d'annulation en attente. Une session qui atteint le seuil après
              la vague attend votre approbation. Doublons impossibles : un envoi par enfant et par session.
            </p>
          </div>
          <button
            onClick={charger}
            className="flex items-center space-x-1 text-sm text-gray-600 hover:text-gray-900"
            title="Actualiser"
          >
            <RefreshCw className={`h-4 w-4 ${chargement ? 'animate-spin' : ''}`} />
            <span>Actualiser</span>
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-3 text-sm">
          {r.envois_suspendus ? (
            <span className="px-3 py-1 rounded-full font-medium bg-red-100 text-red-800">Envois suspendus</span>
          ) : (
            <span className="px-3 py-1 rounded-full font-medium bg-green-100 text-green-800">Envois actifs</span>
          )}
          {isAdmin && (
            <button
              onClick={() => regler({ p_suspendre: !r.envois_suspendus })}
              disabled={action !== null}
              className="px-3 py-1 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
            >
              {r.envois_suspendus ? 'Reprendre les envois' : 'Suspendre les envois'}
            </button>
          )}
          <span className="text-gray-600">
            {etat.envoyes_24h} courriel{etat.envoyes_24h > 1 ? 's' : ''} sur 24 h (plafond {r.plafond_quotidien})
            {r.dernier_rattrapage_le && <> · dernier rattrapage le {jourCourt(r.dernier_rattrapage_le)}</>}
          </span>
        </div>

        {r.pause_jusqu_a && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-900 flex flex-wrap items-center gap-3">
            <PauseCircle className="h-5 w-5" />
            <span>
              Gmail a freiné les envois : reprise le {dateHeure(r.pause_jusqu_a)}.
              {r.pause_motif && <span className="block text-xs text-orange-700 mt-1">{r.pause_motif}</span>}
            </span>
            {isAdmin && (
              <button
                onClick={() => regler({ p_lever_la_pause: true })}
                disabled={action !== null}
                className="ml-auto px-3 py-1 text-sm rounded-lg border border-orange-300 hover:bg-orange-100 disabled:opacity-50"
              >
                Lever la pause
              </button>
            )}
          </div>
        )}

        {alertes.map(a => (
          <div key={a} className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 font-medium flex items-start">
            <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
            <span>{a}</span>
          </div>
        ))}
        {message && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800 flex items-start">
            <CheckCircle className="h-5 w-5 mr-2 flex-shrink-0" />
            <span>{message}</span>
          </div>
        )}
        {erreur && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 flex items-start">
            <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
            <span>{erreur}</span>
          </div>
        )}
      </div>

      {semaines.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <Calendar className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune session à venir</h3>
          <p>Aucune session ne commence dans les 14 prochains jours.</p>
        </div>
      )}

      {/* Une section par semaine */}
      {semaines.map(semaine => {
        const famillesSousLeSeuil = semaine.sousLeSeuil.reduce((n, s) => n + s.familles_sans_info, 0)
        return (
          <section key={semaine.debut} className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Semaine du {jourCourt(semaine.debut)}
                  <span className="ml-2 text-sm font-normal text-gray-500">{libelleJours(joursEntre(aujourdHui, semaine.debut))}</span>
                </h3>
                <p className="text-sm text-gray-600">
                  {semaine.lancee ? 'Première vague lancée : rattrapage chaque matin jusqu\'au premier jour.' : 'Première vague non lancée.'}
                  {famillesSousLeSeuil > 0 && (
                    <> {semaine.sousLeSeuil.length} session(s) sous le seuil : {famillesSousLeSeuil} famille(s) sans information{isAdmin ? ' (« Forcer l\'envoi » pour les servir)' : ''}.</>
                  )}
                </p>
              </div>
              {boutonDeSemaine(semaine)}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {semaine.sessions.map(s => {
                const sit = situation(s, semaine.lancee)
                const couleur =
                  sit === 'servie' ? 'border-green-200 bg-green-50'
                    : sit === 'sous_le_seuil' || sit === 'suspendue' ? 'border-yellow-200 bg-yellow-50'
                      : sit === 'a_approuver' || sit === 'a_lancer' ? 'border-orange-200 bg-orange-50'
                        : 'border-blue-200 bg-blue-50'
                return (
                  <div key={s.session_id} className={`border rounded-lg p-4 ${couleur}`}>
                    <h4 className="font-semibold text-gray-900 flex items-center">
                      <Calendar className="h-4 w-4 mr-2" />
                      {s.stage_title ?? 'Stage inconnu'}
                    </h4>
                    <div className="mt-2 space-y-1 text-sm text-gray-700">
                      <div className="flex items-center"><MapPin className="h-4 w-4 mr-2 text-gray-500" />{s.center_name}</div>
                      <div className="flex items-center"><Clock className="h-4 w-4 mr-2 text-gray-500" />{s.periode} - {s.semaine}</div>
                      <div className="flex items-center">
                        <Users className="h-4 w-4 mr-2 text-gray-500" />
                        {s.enfants_attendus} enfant{s.enfants_attendus > 1 ? 's' : ''} attendu{s.enfants_attendus > 1 ? 's' : ''} ({s.familles_attendues} famille{s.familles_attendues > 1 ? 's' : ''})
                      </div>
                    </div>
                    <div className="mt-3 text-sm font-medium text-gray-900">{libelleSituation(s, semaine.lancee, r)}</div>
                    {s.lancee_le && (
                      <div className="text-xs text-gray-600">
                        {s.lancee_origine === 'approbation' ? 'Approuvée' : 'Lancée'} le {dateHeure(s.lancee_le)}
                      </div>
                    )}
                    <div className="mt-2 flex flex-wrap gap-3 text-xs">
                      <span className="flex items-center text-green-700"><CheckCircle className="h-3 w-3 mr-1" />{s.envoyes} envoyé{s.envoyes > 1 ? 's' : ''}</span>
                      {s.en_attente > 0 && <span className="flex items-center text-blue-700"><Clock className="h-3 w-3 mr-1" />{s.en_attente} en attente</span>}
                      {s.echecs > 0 && <span className="flex items-center text-red-700"><XCircle className="h-3 w-3 mr-1" />{s.echecs} échec{s.echecs > 1 ? 's' : ''}</span>}
                    </div>
                    {s.derniere_erreur && (
                      <p className="mt-1 text-xs text-red-700 break-words">Dernière erreur : {s.derniere_erreur}</p>
                    )}
                    <div className="mt-3 flex flex-wrap gap-2">
                      {s.familles_attendues > 0 && (
                        <button
                          onClick={() => setEnTest(s)}
                          className="flex items-center px-3 py-1 text-xs rounded border border-blue-300 text-blue-700 bg-white hover:bg-blue-50"
                          title="Voir le courriel d'une famille et vous l'envoyer, sans écrire à personne"
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          Tester
                        </button>
                      )}
                      {sit === 'a_approuver' && (
                        <button
                          onClick={() => lancer(semaine.debut, s, `Approuver l'envoi pour ${s.stage_title ?? 'cette session'} (${s.center_name}) ?`)}
                          disabled={action !== null || r.envois_suspendus}
                          className="px-3 py-1 text-xs rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                        >
                          {action === s.session_id ? 'Approbation…' : "Approuver l'envoi"}
                        </button>
                      )}
                      {isAdmin && (
                        <>
                          {sit === 'sous_le_seuil' && s.familles_attendues > 0 && (
                            <button
                              onClick={() => forcer(s)}
                              disabled={action !== null || r.envois_suspendus || s.start_date.slice(0, 10) < aujourdHui}
                              title={
                                r.envois_suspendus ? 'Les envois sont suspendus : relâchez le frein d\'abord'
                                  : s.start_date.slice(0, 10) < aujourdHui ? 'La session a déjà commencé'
                                    : 'Envoyer le message pré-session malgré le seuil'
                              }
                              className="px-3 py-1 text-xs rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {action === s.session_id ? 'Envoi…' : "Forcer l'envoi"}
                            </button>
                          )}
                          <button
                            disabled
                            title={RAISON_SUSPENDRE_FERME}
                            className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Suspendre
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </section>
        )
      })}
    </div>
  )
}
